import { loadCollection, previewUrl } from '@theme/excello-journey';
import { classify, diversify } from '@theme/excello-match';

const bands = { under:[0,29999], everyday:[30000,49999], elevated:[50000,74999], premium:[75000,99999], signature:[100000,Infinity] };
const swatches = {'black':'#202328','white':'#fcfcf9','navy':'#21314a','navy blue':'#21314a','navy blue melange':'#38465a','bottle green':'#264d3d','maroon':'#6b293b','olive':'#686b43','beige':'#d8c8ae','off white':'#ece8dc','off-white':'#ece8dc','royal blue':'#315eb0','lavender':'#b9acd2','light heather grey':'#c6c7c9','cement grey':'#969da3','grey':'#a4a7aa','gray':'#a4a7aa','air force blue':'#69869c','blue':'#5f89b0','still blue':'#719baa','sea green':'#8db5a6','onion':'#bda4a4','mustard':'#c4a349','pastel yellow':'#e9daa1','yellow':'#e4c667','red':'#ab4050','pink':'#d6a6b6','purple':'#89729f','green':'#528467'};

function continuous(host, button, next, signal) {
  if (!('IntersectionObserver' in window)) return;
  let busy=false, paused=false;
  const observer=new IntersectionObserver(async entries=>{
    if(busy||paused||button.hidden||button.disabled||!entries.some(entry=>entry.isIntersecting))return;
    busy=true;
    const before=host.querySelector('ul')?.childElementCount;
    await next();
    paused=!button.hidden&&host.querySelector('ul')?.childElementCount===before;
    busy=false;
  },{rootMargin:'300px 0px'});
  observer.observe(button);
  host.addEventListener('change',()=>{paused=false;},{signal});
  button.addEventListener('click',()=>{paused=false;},{signal});
  signal.addEventListener('abort',()=>observer.disconnect(),{once:true});
}

function revealSelection(rail){
  const active=rail?.querySelector('[aria-pressed="true"]');
  if(!active||rail.scrollWidth<=rail.clientWidth)return;
  const chip=active.getBoundingClientRect(),view=rail.getBoundingClientRect();
  if(chip.left<view.left||chip.right>view.right)rail.scrollBy({left:chip.left-view.left-(view.width-chip.width)/2,behavior:'auto'});
}

async function enhanceColour(host) {
  if(host.dataset.discoveryReady)return;
  host.dataset.discoveryReady='true';
  await customElements.whenDefined('excello-colour-results');
  if(!host.isConnected)return;
  discoveryControls(host, host.abort.signal);
  const signal=host.abort.signal,select=host.querySelector('[data-shade-colour]'),rail=host.querySelector('[data-discovery-colours]'),clear=host.querySelector('[data-shade-clear]'),sort=host.querySelector('[data-shade-sort]');
  sort.value=new URL(location.href).searchParams.get('sort_by')||'manual';
  if(!sort.value)sort.value='manual';
  function sync(){
    for(const chip of rail.children)chip.setAttribute('aria-pressed',String(chip.dataset.colour===select.value));
    clear.hidden=!host.colour;
    revealSelection(rail);
  }
  function chips(){
    const focused=document.activeElement?.dataset.colour;
    const options=[...select.options].filter(option=>option.value);
    rail.replaceChildren();
    for(const option of options){
      const chip=document.createElement('button');chip.type='button';chip.className='excello-discovery__chip';chip.dataset.colour=option.value;
      const swatch=document.createElement('span');swatch.className='excello-discovery__swatch';swatch.style.setProperty('--swatch',swatches[option.value]||'#b1bdc7');swatch.setAttribute('aria-hidden','true');
      const name=document.createElement('span');name.textContent=option.textContent;
      const check=document.createElement('span');check.className='excello-discovery__check';check.textContent='✓';check.setAttribute('aria-hidden','true');chip.append(swatch,name,check);rail.append(chip);
    }
    host.querySelector('[data-discovery-native]').hidden=options.length>0;
    sync();
    if(focused)rail.querySelector(`button[data-colour="${CSS.escape(focused)}"]`)?.focus({preventScroll:true});
  }
  const observer=new MutationObserver(chips);observer.observe(select,{childList:true});signal.addEventListener('abort',()=>observer.disconnect(),{once:true});chips();
  rail.addEventListener('click',event=>{
    const chip=event.target.closest('[data-colour]');if(!chip)return;
    select.value=chip.dataset.colour;select.dispatchEvent(new Event('change',{bubbles:true}));sync();
  },{signal});
  select.addEventListener('change',sync,{signal});
  clear.addEventListener('click',()=>{
    select.value='';host.colour='';const url=new URL(location.href);url.searchParams.delete('excello_colour');url.searchParams.delete('page');history.replaceState(null,'',url);
    host.querySelector('[data-shade-heading]').textContent=host.words.all;host.filter();sync();
  },{signal});
  sort.addEventListener('change',()=>{
    const url=new URL(location.href);url.searchParams.set('sort_by',sort.value);url.searchParams.delete('page');history.replaceState(null,'',url);host.filter();
  },{signal});
  continuous(host,host.querySelector('[data-shade-more]'),()=>host.next(),signal);
}

class ExcelloPriceResults extends HTMLElement {
  connectedCallback(){
    if(this.started)return;this.started=true;this.abort=new AbortController();
    this.words=JSON.parse(this.querySelector('[data-price-copy]').textContent);
    this.params=new URL(location.href).searchParams;
    this.gender=this.querySelector('[data-price-gender]');this.sort=this.querySelector('[data-price-sort]');
    this.gender.value=['men','women','unisex'].includes(this.params.get('excello_gender'))?this.params.get('excello_gender'):'';
    this.sort.value=this.params.get('sort_by')||'manual';if(!this.sort.value)this.sort.value='manual';
    this.low=this.params.has('filter.v.price.gte')?Math.round(Number(this.params.get('filter.v.price.gte'))*100):0;
    this.high=this.params.has('filter.v.price.lte')?Math.round(Number(this.params.get('filter.v.price.lte'))*100):Infinity;
    this.active=Object.entries(bands).find(([,range])=>range[0]===this.low&&((!Number.isFinite(range[1])&&!Number.isFinite(this.high))||Math.floor(range[1]/100)===Math.floor(this.high/100)))?.[0]||'';
    if(this.active)[this.low,this.high]=bands[this.active];
    this.list=this.querySelector('[data-price-list]');this.more=this.querySelector('[data-price-more]');this.status=this.querySelector('[data-price-status]');
    const signal=this.abort.signal;discoveryControls(this,signal);
    this.addEventListener('click',event=>{const band=event.target.closest('[data-price-band]');if(band){this.active=band.dataset.priceBand;[this.low,this.high]=bands[this.active];this.updateURL();this.filter();}},{signal});
    this.querySelector('[data-price-clear]').addEventListener('click',()=>{this.active='';this.low=0;this.high=Infinity;this.updateURL();this.filter();},{signal});
    this.gender.addEventListener('change',()=>{this.updateURL();this.filter();},{signal});
    this.sort.addEventListener('change',()=>{this.updateURL();this.filter();},{signal});
    this.more.addEventListener('click',()=>this.next(),{signal});
    this.updateURL();this.sync();discoveryReset(this,this.list,this.more,this.status,this.words);this.start();continuous(this,this.more,()=>this.next(),signal);
  }
  disconnectedCallback(){this.abort?.abort();this.started=false;}
  updateURL(){
    const url=new URL(location.href);url.searchParams.set('view','excello-price');url.searchParams.set('filter.v.availability','1');url.searchParams.delete('page');url.searchParams.set('sort_by',this.sort.value);
    if(this.gender.value)url.searchParams.set('excello_gender',this.gender.value);else url.searchParams.delete('excello_gender');
    if(this.active||this.low>0)url.searchParams.set('filter.v.price.gte',String(this.low/100));else url.searchParams.delete('filter.v.price.gte');
    if(Number.isFinite(this.high))url.searchParams.set('filter.v.price.lte',String(this.high/100));else url.searchParams.delete('filter.v.price.lte');
    history.replaceState(null,'',url);
  }
  sync(){
    for(const chip of this.querySelectorAll('[data-price-band]'))chip.setAttribute('aria-pressed',String(chip.dataset.priceBand===this.active));
    revealSelection(this.querySelector('.excello-discovery__prices'));
    this.querySelector('[data-price-clear]').hidden=!this.active&&this.low===0&&!Number.isFinite(this.high);
  }
  eligible(item){return item.available&&!item.hidden&&['men','women','unisex'].includes(item.gender)&&(!this.gender.value||item.gender===this.gender.value)&&Number.isFinite(item.price)&&item.price>=this.low&&item.price<=this.high;}
  async start(){
    try{this.pool=await loadCollection('all',this.dataset.root);if(!this.abort.signal.aborted)await this.filter();}
    catch{this.status.textContent=this.words.retry;}
  }
  async filter(){
    this.sync();this.generation=(this.generation||0)+1;this.items=(this.pool||[]).filter(item=>this.eligible(item));
    const order=this.sort.value;
    if(order==='price-ascending')this.items.sort((a,b)=>a.price-b.price||String(a.id).localeCompare(String(b.id)));
    else if(order==='price-descending')this.items.sort((a,b)=>b.price-a.price||String(a.id).localeCompare(String(b.id)));
    else if(order==='title-ascending')this.items.sort((a,b)=>a.title.localeCompare(b.title));
    else this.items=diversify(this.items);
    discoveryReset(this,this.list,this.more,this.status,this.words);
    if(this.pool)await this.next();
  }
  next(){return discoveryNext(this,item=>this.eligible(item));}

}
if(!customElements.get('excello-price-results'))customElements.define('excello-price-results',ExcelloPriceResults);
document.querySelectorAll('excello-colour-results').forEach(enhanceColour);
document.addEventListener('shopify:section:load',event=>event.target.querySelectorAll('excello-colour-results').forEach(enhanceColour));

// Filtering owns its requests and cache; recommendation/Wishlist loaders remain unchanged.
const discoveryCards = new Map();
async function discoveryCard(host, item, signal) {
  const url = previewUrl(new URL(`${host.dataset.root || '/'}products/${item.handle}`, location.origin));
  url.searchParams.set('section_id', 'excello-recommendation-card');
  const key = url.toString();
  if (discoveryCards.has(key)) return discoveryCards.get(key);
  const response = await fetch(url, { signal, credentials: 'same-origin' });
  if (!response.ok) throw new Error('Product card could not load');
  const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
  const data = doc.querySelector('[data-rec-data]'), card = doc.querySelector('[data-rec-card]');
  if (!data || !card) throw new Error('Product card missing');
  const result = { item: classify(JSON.parse(data.textContent)), html: card.innerHTML };
  if (discoveryCards.size >= 96) discoveryCards.delete(discoveryCards.keys().next().value);
  discoveryCards.set(key, result);
  return result;
}

export function discoveryControls(host, signal) {
  const dialog = host.querySelector('[data-discovery-dialog]');
  if (!dialog || dialog.dataset.ready) return;
  dialog.dataset.ready = 'true';
  const opener = host.querySelector('[data-discovery-open]');
  opener.addEventListener('click', () => dialog.showModal(), { signal });
  host.querySelectorAll('[data-discovery-close]').forEach(button => button.addEventListener('click', () => dialog.close(), { signal }));
  dialog.addEventListener('click', event => { if (event.target === dialog) { const box = dialog.getBoundingClientRect(); if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) dialog.close(); } }, { signal });
  dialog.addEventListener('close', () => opener.focus({ preventScroll: true }), { signal });
  signal.addEventListener('abort', () => dialog.close(), { once: true });
  const select = host.querySelector('[data-shade-sort], [data-price-sort], [name="sort_by"]');
  const choices = host.querySelector('[data-discovery-sort-options]');
  if (select && choices) {
    const buttons = [...select.options].map(option => {
      const button = document.createElement('button'); button.type = 'button'; button.setAttribute('role', 'radio'); button.dataset.sortValue = option.value; button.textContent = option.textContent;
      button.addEventListener('click', () => { select.value = option.value; select.dispatchEvent(new Event('change', { bubbles: true })); host.querySelector('[data-discovery-sort]').open = false; }, { signal });
      choices.append(button); return [button, option.value];
    });
    const sync = () => buttons.forEach(([button, value]) => button.setAttribute('aria-checked', String(value === select.value)));
    select.addEventListener('change', sync, { signal }); sync(); select.closest('label').hidden = true;
    choices.addEventListener('keydown', event => {
      if (!['ArrowUp','ArrowDown','Home','End'].includes(event.key)) return;
      event.preventDefault(); const current = buttons.findIndex(([button]) => button === event.target);
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length - 1 : Math.max(0, Math.min(buttons.length - 1, current + (event.key === 'ArrowDown' ? 1 : -1)));
      buttons[next][0].focus();
    }, { signal });
  }
}

export function discoveryReset(host, list, button, status, words) {
  const previous = host.resultState;
  previous?.request.abort();
  const reservedHeight = list.getBoundingClientRect().height;
  const originalMinHeight = previous?.originalMinHeight ?? list.style.minHeight;
  if (reservedHeight > 0) list.style.minHeight = `${reservedHeight}px`;
  const state = { originalMinHeight, started: performance.now(), items: host.items || [], offset: 0, seen: new Set(), request: new AbortController(), loading: false, list, button, status, words };
  host.resultState = state;
  host.abort.signal.addEventListener('abort', () => state.request.abort(), { once: true, signal: state.request.signal });
  button.hidden = true; list.setAttribute('aria-busy', 'true'); status.textContent = words.loading;
  const placeholders = Array.from({ length: 8 }, () => { const li = document.createElement('li'); li.className = 'excello-filter-skeleton'; li.dataset.discoverySkeleton = ''; li.setAttribute('aria-hidden','true'); return li; });
  list.replaceChildren(...placeholders);
  const sort = host.querySelector('[data-shade-sort], [data-price-sort], [name="sort_by"]');
  host.querySelectorAll('[data-sort-value]').forEach(button => button.setAttribute('aria-checked', String(button.dataset.sortValue === sort?.value)));
  const view = host.querySelector('[data-discovery-view]');
  if (view) view.textContent = view.dataset.copy.replace('__COUNT__', String(state.items.length));
  const toolbar = host.querySelector('.excello-discovery__panel');
  if (toolbar?.getBoundingClientRect().top < 0 && !host.querySelector('dialog[open]')) toolbar.scrollIntoView({ block:'start', behavior:'instant' });
  return state;
}

export async function discoveryNext(host, eligible) {
  const state = host.resultState;
  if (!state || state.loading || state.request.signal.aborted) return;
  state.loading = true; state.button.disabled = true;
  const current = () => host.isConnected && host.resultState === state && !state.request.signal.aborted;
  try {
    let added = 0;
    while (state.offset < state.items.length && added < 24 && current()) {
      const batch = state.items.slice(state.offset, state.offset + 24 - added); state.offset += batch.length;
      // Six bounded workers; paint in the existing order as soon as each card is ready.
      let cursor = 0;
      const slots = batch.map(() => { let resolve; const promise = new Promise(done => { resolve = done; }); return { promise, resolve }; });
      const worker = async () => { while (cursor < batch.length && current()) { const index = cursor++; let result; try { result = await discoveryCard(host, batch[index], state.request.signal); } catch (error) { result = { error }; } slots[index].resolve(result); } };
      const workers = Array.from({ length: Math.min(6, batch.length) }, worker);
      for (const slot of slots) {
        if (!current()) return;
        const result = await slot.promise;
        if (!current()) return;
        if (result?.error) throw result.error;
        const item = result?.item;
        if (!item || !eligible(item) || state.seen.has(String(item.id))) continue;
        state.seen.add(String(item.id));
        const li = document.createElement('li'); li.dataset.handle = item.handle; li.dataset.productId = String(item.id); li.dataset.family = item.family || ''; li.dataset.colour = item.colour; li.dataset.gender = item.gender; li.dataset.price = String(item.price); li.innerHTML = result.html;
        for (const link of li.querySelectorAll('a[href]')) link.href = previewUrl(link.href).href;
        for (const [index, image] of [...li.querySelectorAll('img')].entries()) { image.decoding = 'async'; image.sizes = '(min-width:1200px) 300px, (min-width:750px) 30vw, 46vw'; image.loading = state.seen.size <= 4 && index === 0 ? 'eager' : 'lazy'; if (state.seen.size <= 2 && index === 0) image.setAttribute('fetchpriority','high'); else image.removeAttribute('fetchpriority'); }
        const placeholder = state.list.querySelector('[data-discovery-skeleton]');
        if (placeholder) placeholder.replaceWith(li);
        else state.list.append(li);
        added++;
        if (state.seen.size === 1) host.dataset.filterFirstReadyMs = String(Math.round(performance.now() - state.started));
      }
      await Promise.all(workers);
    }
    if (!current()) return;
    state.list.querySelectorAll('[data-discovery-skeleton]').forEach(node => node.remove());
    state.list.style.minHeight = state.originalMinHeight;
    state.button.hidden = state.offset >= state.items.length; state.status.textContent = state.list.childElementCount ? '' : state.words.empty;
  } catch (error) {
    if (current()) { state.status.textContent = state.words.retry || state.words.error; state.button.hidden = false; state.offset = Math.max(0, state.offset - 24); }
  } finally {
    if (current()) { state.button.disabled = false; state.loading = false; state.list.removeAttribute('aria-busy'); host.dataset.filterReadyMs = String(Math.round(performance.now() - state.started)); }
  }
}

